import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { Calendar, Clock, PartyPopper } from "lucide-react";
import { format, differenceInDays, startOfDay } from "date-fns";
import { Link } from "react-router-dom";

import { getActiveCompanyHolidayListForEmployeeQueryOptions } from "@/config/queries/emp-query-options";
import { Badge } from "@/components/ui/badge/badge";
import { Button } from "@/components/ui/button/button";
import { Skeleton } from "@/components/ui/skeleton/skeleton";

interface Holiday {
  holidayName: string;
  date: string | Date;
  description?: string;
  isHalfDay: boolean;
  minimumHours?: number;
}

export const UpcomingHolidayCard = () => {
  const key = Cookies.get("key") as string;
  const currentYear = new Date().getFullYear();

  const { data, isPending } = useQuery(
    getActiveCompanyHolidayListForEmployeeQueryOptions(key, currentYear)
  );

  // Get upcoming holidays within 7 days (excluding Sundays and today)
  const upcomingHolidays = useMemo(() => {
    if (!data?.foundList?.holidays) {
      return [];
    }

    const today = startOfDay(new Date());

    return data.foundList.holidays
      .filter((holiday: Holiday) => {
        const holidayDate = startOfDay(new Date(holiday.date));
        const daysUntil = differenceInDays(holidayDate, today);

        // Check if it's a Sunday
        const isSunday = holidayDate.getDay() === 0;

        // Include if: within next 7 days (1-7), not today, not a Sunday
        return daysUntil > 0 && daysUntil <= 7 && !isSunday;
      })
      .sort(
        (a: Holiday, b: Holiday) =>
          new Date(a.date).getTime() - new Date(b.date).getTime()
      );
  }, [data]);

  // Don't render if no upcoming holidays
  if (isPending) {
    return <Skeleton className="w-full max-w-md h-24" />;
  }

  if (upcomingHolidays.length === 0) {
    return null;
  }

  // Get the nearest upcoming holiday
  const nearestHoliday = upcomingHolidays[0];
  const holidayDate = new Date(nearestHoliday.date);
  const daysUntil = differenceInDays(startOfDay(holidayDate), startOfDay(new Date()));

  return (
    <div
      className={`w-full max-w-md rounded-lg border-2 shadow-sm p-4 ${
        nearestHoliday.isHalfDay
          ? "border-orange-400 bg-orange-50 dark:bg-orange-950/20"
          : "border-green-400 bg-green-50 dark:bg-green-950/20"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <PartyPopper
            className={`h-5 w-5 ${
              nearestHoliday.isHalfDay ? "text-orange-600" : "text-green-600"
            }`}
          />
          <span
            className={`text-sm font-semibold ${
              nearestHoliday.isHalfDay
                ? "text-orange-700 dark:text-orange-400"
                : "text-green-700 dark:text-green-400"
            }`}
          >
            Upcoming Holiday
          </span>
        </div>
        <Badge
          variant="outline"
          className={
            nearestHoliday.isHalfDay
              ? "border-orange-500 text-orange-600 bg-orange-100 dark:bg-orange-900/30"
              : "border-green-500 text-green-600 bg-green-100 dark:bg-green-900/30"
          }
        >
          {nearestHoliday.isHalfDay ? "Half Day" : "Full Holiday"}
        </Badge>
      </div>

      {/* Holiday Name */}
      <h3
        className={`text-lg font-bold mb-2 ${
          nearestHoliday.isHalfDay
            ? "text-orange-800 dark:text-orange-300"
            : "text-green-800 dark:text-green-300"
        }`}
      >
        {nearestHoliday.holidayName}
      </h3>

      {/* Date and Days Info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{format(holidayDate, "EEE, MMM d")}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>
              {daysUntil === 1 ? "Tomorrow" : `In ${daysUntil} days`}
            </span>
          </div>
        </div>

        <Button variant="ghost" size="sm" asChild className="text-xs">
          <Link to="/employee/company-holidays">View All</Link>
        </Button>
      </div>

      {/* Half-day info */}
      {nearestHoliday.isHalfDay && nearestHoliday.minimumHours && (
        <p className="mt-2 text-xs text-orange-600 dark:text-orange-400">
          Minimum {nearestHoliday.minimumHours} hours of work required
        </p>
      )}

      {/* Show if there are more upcoming holidays */}
      {upcomingHolidays.length > 1 && (
        <p className="mt-2 text-xs text-muted-foreground">
          +{upcomingHolidays.length - 1} more holiday
          {upcomingHolidays.length - 1 > 1 ? "s" : ""} this week
        </p>
      )}
    </div>
  );
};
